/**
 * Maps whatever a create/update submit failed with onto something the
 * aquarium form can show *translated*. Raw backend / zod text (English,
 * e.g. "Expected boolean, received null") is never shown to the user.
 */

export type AquariumFieldName =
  | 'name'
  | 'description'
  | 'tankType'
  | 'setupDate'
  | 'grossVolume'
  | 'netVolume'
  | 'length'
  | 'width'
  | 'height'
  | 'sumpAvailable'
  | 'supplySystem'
  | 'equipment'
  | 'equipmentNotes'
  | 'notes';

/** Backend (snake_case) body field → form field. */
const BACKEND_FIELD_MAP: Record<string, AquariumFieldName> = {
  name: 'name',
  description: 'description',
  tank_type: 'tankType',
  setup_date: 'setupDate',
  gross_volume: 'grossVolume',
  net_volume: 'netVolume',
  length: 'length',
  width: 'width',
  height: 'height',
  sump_available: 'sumpAvailable',
  supply_system: 'supplySystem',
  equipment: 'equipment',
  equipment_notes: 'equipmentNotes',
  notes: 'notes',
};

export type SubmitErrorInfo =
  /** A message the caller already translated (plain string prop). */
  | { kind: 'message'; message: string }
  /** Backend rejected the input (400 VALIDATION_ERROR), optionally for one field. */
  | { kind: 'validation'; field?: AquariumFieldName }
  /** Anything else (network, 5xx, …). */
  | { kind: 'failed' };

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

export function classifySubmitError(error: unknown): SubmitErrorInfo | null {
  if (error == null || error === '') return null;
  if (typeof error === 'string') return { kind: 'message', message: error };

  const err = asRecord(error);
  if (err?.code === 'VALIDATION_ERROR') {
    const body = asRecord(asRecord(err.data)?.error);
    const backendField = typeof body?.field === 'string' ? body.field : undefined;
    const field = backendField ? BACKEND_FIELD_MAP[backendField] : undefined;
    return field ? { kind: 'validation', field } : { kind: 'validation' };
  }
  return { kind: 'failed' };
}
